/*
 * Copyright (c) Hilo 2020.
 * Author: fharold
 *
 * hilo-oauth-manager
 * HomePage.tsx
 */

import {PolicyListener} from "../../services/PolicyService";
import Policy from "../../tools/provider/Policy";
import HiloReactComponent from "../../tools/view/layout/HiloReactComponent";
import React from "react";
import PageTitle, {TitleTab} from "../../tools/view/layout/PageTitle";
import Client from "../../models/Client";
import ClientAdapter, {ClientAdapterListener} from "../adapter/ClientAdapter";
import ClientService from "../../services/ClientService";
import ClientForm from "../../models/form/client/ClientForm";
import UIToast from "../../tools/view/toast/UIToast";
import FormModal from "../../tools/view/layout/FormModal";
import ReactDOM from "react-dom";
import NavigationService from "../../tools/service/NavigationService";
import List from "../../tools/list/List";
import Spinner from "../../tools/view/element/Spinner";
import ClientDataProvider from "../../provider/ClientDataProvider";
import {GrantType} from "../../tools/model/auth/GrantType";
import PageComponent from "../../tools/view/layout/PageComponent";

interface State {
    policy: Policy;
    pageIndex: number;
    clientDataProvider: ClientDataProvider;
    clientAdapter?: ClientAdapter;
}

export default class ClientsPage extends HiloReactComponent<{}, State> implements PolicyListener, ClientAdapterListener {

    private readonly _navigationService: NavigationService;
    private readonly _clientService: ClientService;
    private _modalContainer?: Element;
    private _clientForm: ClientForm;

    /**
     * constructor
     * @param props
     * @param state
     */
    constructor(props: {}, state: State) {
        super(props, state);

        this._navigationService = this._serviceRepository.resolve(NavigationService.id) as NavigationService;
        this._clientService = this._serviceRepository.resolve(ClientService.id) as ClientService;

        const clientDataProvider = new ClientDataProvider(this._clientService);

        this.state = {
            policy: this._policyService.policy,
            pageIndex: 0,
            clientDataProvider: clientDataProvider,
            clientAdapter: new ClientAdapter(this.t, clientDataProvider)
        };

        this._clientForm = new ClientForm(Object.values(GrantType))
    }

    /**
     * rowSelected
     * @param obj
     */
    public rowSelected(obj: Client): void {
        this._navigationService.load(`/client/${obj.id}`)
    }

    /**
     * componentDidMount
     */
    public componentDidMount(): void {
        super.componentDidMount();
        this._clientService.addListener(this);
        this._modalContainer = document.querySelector('#modal-container') || undefined;
        this._policyService.addListener(this);

        if (this.state.clientAdapter) {
            this.state.clientAdapter.listener = this;
        }

        // this.state.clientDataProvider.subscribe(this);
    }

    /**
     * componentWillUnmount
     */
    public componentWillUnmount(): void {
        this._policyService.removeListener(this);
        this._clientService.removeListener(this);

        // this.state.clientDataProvider.unsubscribe(this);

        super.componentWillUnmount();
    }

    /**
     * policyUpdated
     * @param policy
     */
    public policyUpdated = (policy: Policy) => {
        this.setState({
            policy: policy
        })
    };

    /**
     * _displayAddUserModal
     * @private
     */
    private _displayAddUserModal = async () => {
        if (!this._modalContainer || !this._clientForm) {
            return;
        }

        const modal = <FormModal
            emptyForm={this._clientForm}
            title={t => t.newClient}
            onSaveClicked={this._saveClient}
        />;

        ReactDOM.render(modal, this._modalContainer);
    }

    /**
     * _clientList
     * @returns {any}
     * @private
     */
    private _clientList = (props: { provider?: ClientDataProvider, adapter?: ClientAdapter }) => {
        const {provider, adapter} = props;

        if (!adapter || !provider) return <Spinner/>;

        return <List adapter={adapter}
                     dataProvider={provider}
                     onAddClick={this._displayAddUserModal}/>
    }

    /**
     * _saveClient
     * @param dto
     * @private
     */
    private _saveClient = async (dto: FormData) => {
        try {
            await this._clientService.createClient(dto);
            if (this._clientForm) this._clientForm.statusCode = 201;
            UIToast.default.show(this.t.clientAdded)

            this.state.clientDataProvider.load()

        } catch (e) {
            if (this._clientForm) {
                this._clientForm.statusCode = e.status
            }
            console.error(e);
        }
    };

    /**
     * render
     */
    public render(): any {
        const {clientDataProvider, clientAdapter} = this.state;

        const titleTabs: TitleTab[] = [
            {title: t => t.list, content: <this._clientList provider={clientDataProvider} adapter={clientAdapter}/>}
        ];

        return <PageComponent>
            <PageTitle title={t => t.clients} tabs={titleTabs}
                       onTabSelected={index => this.setState({pageIndex: index})}/>
            {
                titleTabs[this.state.pageIndex].content
            }
        </PageComponent>
    }
}